import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import {
  GetBuildingSettingsGQL,
  GetUnitSettingsGQL,
  GetResidentSettingsGQL,
  UpdateBuildingSettingsGQL,
  UpdateUnitSettingsGQL,
  UpdateResidentSettingsGQL,
  UpdateBuildingSettingsInput,
  UpdateUnitSettingsInput,
  UpdateResidentSettingsInput,
} from 'src/graphql/generated/schema';

export interface BuildingSettings {
  chatbotEnabled?: boolean;
  [key: string]: any;
}

export interface UnitSettings {
  [key: string]: any;
}

export interface ResidentSettings {
  earlyAccess?: boolean;
  surveyTester?: boolean;
  notificationSettings?: {
    muteNotifications?: boolean;
    lowLevelNotificationChannels?: string[];
    mediumLevelNotificationChannels?: string[];
    criticalLevelNotificationChannels?: string[];
    energyConsumptionNotifications?: boolean;
    severeWeatherNotifications?: boolean;
  };
  packageNotificationSettings?: {
    enabled?: boolean;
    push?: boolean;
    email?: boolean;
    sms?: boolean;
    visualAlert?: string; // "flash" or null/undefined (following current implementation pattern)
    audioAlert?: string; // "siren" or null/undefined (following current implementation pattern)
  };
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class FeatureSettingsService {
  private readonly RESIDENT_SETTINGS_CACHE_KEY = 'cached_resident_settings';
  private readonly BUILDING_SETTINGS_CACHE_KEY = 'cached_building_settings';

  private buildingSettings$ = new BehaviorSubject<BuildingSettings | null>(null);
  private unitSettings$ = new BehaviorSubject<UnitSettings | null>(null);
  private residentSettings$ = new BehaviorSubject<ResidentSettings | null>(null);
  private residentSettingsLoaded = false; // Track if resident settings have been loaded from server

  constructor(
    private getBuildingSettingsGQL: GetBuildingSettingsGQL,
    private getUnitSettingsGQL: GetUnitSettingsGQL,
    private getResidentSettingsGQL: GetResidentSettingsGQL,
    private updateBuildingSettingsGQL: UpdateBuildingSettingsGQL,
    private updateUnitSettingsGQL: UpdateUnitSettingsGQL,
    private updateResidentSettingsGQL: UpdateResidentSettingsGQL,
  ) {
    // Load cached settings immediately for instant availability
    this.loadCachedResidentSettings();
    this.loadCachedBuildingSettings();
  }

  /**
   * Load resident settings from local cache for instant availability on app start
   */
  private loadCachedResidentSettings(): void {
    try {
      const cached = localStorage.getItem(this.RESIDENT_SETTINGS_CACHE_KEY);
      if (cached) {
        const settings = JSON.parse(cached);
        this.residentSettings$.next(settings);
      }
    } catch (e) {
      console.warn('Error loading cached resident settings:', e);
    }
  }

  /**
   * Save resident settings to local cache
   */
  private cacheResidentSettings(settings: ResidentSettings): void {
    try {
      localStorage.setItem(this.RESIDENT_SETTINGS_CACHE_KEY, JSON.stringify(settings));
    } catch (e) {
      console.warn('Error caching resident settings:', e);
    }
  }

  /**
   * Load building settings from local cache for instant availability on app start
   */
  private loadCachedBuildingSettings(): void {
    try {
      const cached = localStorage.getItem(this.BUILDING_SETTINGS_CACHE_KEY);
      if (cached) {
        const settings = JSON.parse(cached);
        this.buildingSettings$.next(settings);
      }
    } catch (e) {
      console.warn('Error loading cached building settings:', e);
    }
  }

  /**
   * Save building settings to local cache
   */
  private cacheBuildingSettings(settings: BuildingSettings): void {
    try {
      localStorage.setItem(this.BUILDING_SETTINGS_CACHE_KEY, JSON.stringify(settings));
    } catch (e) {
      console.warn('Error caching building settings:', e);
    }
  }

  /**
   * Clear all cached settings (call on logout)
   */
  clearCache(): void {
    localStorage.removeItem(this.RESIDENT_SETTINGS_CACHE_KEY);
    localStorage.removeItem(this.BUILDING_SETTINGS_CACHE_KEY);
    this.buildingSettings$.next(null);
    this.unitSettings$.next(null);
    this.residentSettings$.next(null);
  }

  /**
   * Load all settings from the backend
   */
  loadAllSettings(): void {
    this.loadBuildingSettings();
    this.loadUnitSettings();
    this.loadResidentSettings();
  }

  /**
   * Load building settings
   */
  private loadBuildingSettings(): void {
    this.getBuildingSettingsGQL.fetch().subscribe({
      next: (result) => {
        if (result.data?.getBuildingSettings) {
          try {
            const settings = JSON.parse(result.data.getBuildingSettings);
            this.buildingSettings$.next(settings);
            this.cacheBuildingSettings(settings);
          } catch (e) {
            console.error('Error parsing building settings JSON:', e);
            this.buildingSettings$.next({});
          }
        } else {
          this.buildingSettings$.next({});
        }
      },
      error: (error) => {
        console.error('Error loading building settings:', error);
        // Don't clear cache on error - keep showing cached data
      }
    });
  }

  /**
   * Load unit settings
   */
  private loadUnitSettings(): void {
    this.getUnitSettingsGQL.fetch().subscribe({
      next: (result) => {
        if (result.data?.getUnitSettings) {
          try {
            const settings = JSON.parse(result.data.getUnitSettings);
            this.unitSettings$.next(settings);
          } catch (e) {
            console.error('Error parsing unit settings JSON:', e);
            this.unitSettings$.next({});
          }
        } else {
          this.unitSettings$.next({});
        }
      },
      error: (error) => {
        console.error('Error loading unit settings:', error);
        this.unitSettings$.next({});
      }
    });
  }

  /**
   * Load resident settings
   */
  private loadResidentSettings(): void {
    this.getResidentSettingsGQL.fetch().subscribe({
      next: (result) => {
        if (result.data?.getResidentSettings) {
          try {
            const settings = JSON.parse(result.data.getResidentSettings);
            this.residentSettings$.next(settings);
            this.cacheResidentSettings(settings);
            this.residentSettingsLoaded = true;
          } catch (e) {
            console.error('Error parsing resident settings JSON:', e);
            this.residentSettings$.next({});
            this.residentSettingsLoaded = true;
          }
        } else {
          this.residentSettings$.next({});
          this.residentSettingsLoaded = true;
        }
      },
      error: (error) => {
        console.error('Error loading resident settings:', error);
        // Don't clear cache on error - keep showing cached data
        this.residentSettingsLoaded = true;
      }
    });
  }

  /**
   * Get a setting value with hierarchical resolution
   * Priority: Resident > Unit > Building
   * @param key Setting key (e.g., 'chatbotEnabled')
   * @returns Observable of the setting value
   */
  getSetting<T = any>(key: string): Observable<T | undefined> {
    return combineLatest([
      this.residentSettings$,
      this.unitSettings$,
      this.buildingSettings$
    ]).pipe(
      map(([resident, unit, building]) => {
        // Resident overrides unit overrides building
        if (resident && key in resident) {
          return resident[key];
        }
        if (unit && key in unit) {
          return unit[key];
        }
        if (building && key in building) {
          return building[key];
        }
        return undefined;
      })
    );
  }

  /**
   * Check if chatbot is enabled
   * Now defaults to true (fully released feature)
   * Can still be disabled per building/unit/resident if needed
   * @returns Observable<boolean>
   */
  isChatbotEnabled(): Observable<boolean> {
    return this.getSetting<boolean>('chatbotEnabled').pipe(
      map(value => value !== false) // Default to true, only false if explicitly disabled
    );
  }

  /**
   * Check if early access is enabled for the resident
   * @returns Observable<boolean>
   */
  /**
   * VI-190 chat feedback prompt. Default OFF so it can be piloted per building
   * — there is no dev environment for the chat backend.
   */
  isChatFeedbackEnabled(): Observable<boolean> {
    return this.getSetting<boolean>('chatFeedbackEnabled').pipe(
      map(value => value === true)
    );
  }

  isEarlyAccessEnabled(): Observable<boolean> {
    return this.residentSettings$.pipe(
      map(settings => settings?.['earlyAccess'] === true)
    );
  }

  /**
   * Check if survey tester mode is enabled for the resident
   * When enabled, the user can see test surveys before they go live
   * @returns Observable<boolean>
   */
  isSurveyTesterEnabled(): Observable<boolean> {
    return this.residentSettings$.pipe(
      map(settings => settings?.['surveyTester'] === true)
    );
  }

  /**
   * Check if phone as key (BLE entry portal) is enabled for the resident
   * @returns Observable<boolean>
   */
  isPhoneAsKeyEnabled(): Observable<boolean> {
    return this.residentSettings$.pipe(
      map(settings => settings?.['phoneAsKeyEnabled'] === true)
    );
  }

  /**
   * Check if PIN/Face ID is required on app launch
   * Default is false (OFF) - no PIN required unless explicitly enabled
   * @returns Observable<boolean>
   */
  isRequirePinOnLaunchEnabled(): Observable<boolean> {
    return this.residentSettings$.pipe(
      map(settings => settings?.['requirePinOnLaunch'] === true)
    );
  }

  /**
   * Get require PIN on launch value synchronously from cache
   * Used in sign-in flow where we need immediate access without async
   * @returns boolean
   */
  getRequirePinOnLaunchSync(): boolean {
    try {
      const cached = localStorage.getItem(this.RESIDENT_SETTINGS_CACHE_KEY);
      if (cached) {
        const settings = JSON.parse(cached);
        return settings?.requirePinOnLaunch === true;
      }
    } catch (e) {
      // Ignore cache errors
    }
    return false; // Default: don't require PIN
  }

  /**
   * Get phone as key enabled value synchronously from cache
   * @returns boolean
   */
  getPhoneAsKeyEnabledSync(): boolean {
    try {
      const cached = localStorage.getItem(this.RESIDENT_SETTINGS_CACHE_KEY);
      if (cached) {
        const settings = JSON.parse(cached);
        return settings?.phoneAsKeyEnabled === true;
      }
    } catch (e) {
      // Ignore cache errors
    }
    return false;
  }

  /**
   * Check if consumption alerts feature is enabled
   * Defaults to true (shown), only hides when explicitly set to false
   * @returns Observable<boolean>
   */
  isConsumptionAlertsEnabled(): Observable<boolean> {
    return this.getSetting<boolean>('consumptionAlerts').pipe(
      map(value => value !== false) // Default to true, only false if explicitly disabled
    );
  }

  /**
   * Check if holiday card is enabled
   * Uses cached value for immediate display, then updates from server
   * @returns Observable<boolean>
   */
  isHolidayCardEnabled(): Observable<boolean> {
    // Get cached value synchronously for immediate emission
    const cachedValue = this.getCachedHolidayCardEnabled();

    return this.getSetting<boolean>('holidayCardEnabled').pipe(
      map(value => value === true),
      startWith(cachedValue) // Use cached value to prevent pop-in
    );
  }

  /**
   * Get cached holiday card enabled value synchronously
   * Follows hierarchical resolution: Resident > Building
   */
  private getCachedHolidayCardEnabled(): boolean {
    try {
      // Check resident settings first (higher priority)
      const cachedResident = localStorage.getItem(this.RESIDENT_SETTINGS_CACHE_KEY);
      if (cachedResident) {
        const residentSettings = JSON.parse(cachedResident);
        if ('holidayCardEnabled' in residentSettings) {
          return residentSettings.holidayCardEnabled === true;
        }
      }

      // Fall back to building settings
      const cachedBuilding = localStorage.getItem(this.BUILDING_SETTINGS_CACHE_KEY);
      if (cachedBuilding) {
        const buildingSettings = JSON.parse(cachedBuilding);
        return buildingSettings?.holidayCardEnabled === true;
      }
    } catch (e) {
      // Ignore cache errors
    }
    return false;
  }

  /**
   * Update building settings (admin only)
   * @param buildingId Building ID
   * @param settings Settings object
   */
  async updateBuildingSettings(buildingId: string, settings: BuildingSettings): Promise<boolean> {
    try {
      const input: UpdateBuildingSettingsInput = {
        buildingId,
        settings: JSON.stringify(settings)
      };

      const result = await this.updateBuildingSettingsGQL.mutate({ input }).toPromise();

      if (result?.data?.updateBuildingSettings) {
        // Reload settings after update
        this.loadBuildingSettings();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating building settings:', error);
      return false;
    }
  }

  /**
   * Update unit settings
   * @param unitId Unit ID
   * @param settings Settings object
   */
  async updateUnitSettings(unitId: string, settings: UnitSettings): Promise<boolean> {
    try {
      const input: UpdateUnitSettingsInput = {
        unitId,
        settings: JSON.stringify(settings)
      };

      const result = await this.updateUnitSettingsGQL.mutate({ input }).toPromise();

      if (result?.data?.updateUnitSettings) {
        // Reload settings after update
        this.loadUnitSettings();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating unit settings:', error);
      return false;
    }
  }

  /**
   * Update resident settings
   * @param settings Settings object
   */
  async updateResidentSettings(settings: ResidentSettings): Promise<boolean> {
    try {
      const input: UpdateResidentSettingsInput = {
        settings: JSON.stringify(settings)
      };

      const result = await this.updateResidentSettingsGQL.mutate({ input }).toPromise();

      // GraphQL serializes the enum to its NAME, so both outcomes are non-empty
      // strings. A truthy check treats RESIDENT_NOT_FOUND as success and caches a
      // write that never happened.
      if (result?.data?.updateResidentSettings === 'RESIDENT_SETTINGS_UPDATED') {
        this.residentSettings$.next(settings);
        this.cacheResidentSettings(settings);
        return true;
      }
      console.error('Resident settings not saved:', result?.data?.updateResidentSettings);
      return false;
    } catch (error) {
      console.error('Error updating resident settings:', error);
      return false;
    }
  }

  /**
   * Get raw building settings
   */
  getBuildingSettings(): Observable<BuildingSettings | null> {
    return this.buildingSettings$.asObservable();
  }

  /**
   * Get raw unit settings
   */
  getUnitSettings(): Observable<UnitSettings | null> {
    return this.unitSettings$.asObservable();
  }

  /**
   * Get raw resident settings
   * Automatically loads from server if not already loaded
   */
  getResidentSettings(): Observable<ResidentSettings | null> {
    // Auto-load settings from server if not already loaded
    if (!this.residentSettingsLoaded) {
      this.loadResidentSettings();
    }
    return this.residentSettings$.asObservable();
  }
}
